import React, { useContext, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { Check, Copy, Printer, Trash2, Plus, ShoppingBasket, X } from "lucide-react";
import { ThemeContext } from "../App";
import { EmptyState } from "../components/common/Common";
import { printShoppingList } from "../components/printRecipe";
import { useShoppingList } from "../utils/stores";
import "./ShoppingList.css";

const toast = (icon, title) =>
  Swal.fire({ toast: true, position: "bottom", icon, title, showConfirmButton: false, timer: 1600 });

const ShoppingList = () => {
  const { theme } = useContext(ThemeContext);
  const { items, addCustom, toggle, remove, clearChecked, clearAll } = useShoppingList();
  const [text, setText] = useState("");

  const groups = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.recipeId;
      if (!map.has(key)) map.set(key, { key, title: item.recipe, items: [] });
      map.get(key).items.push(item);
    });
    return Array.from(map.values());
  }, [items]);

  const done = items.filter((i) => i.checked).length;
  const percent = items.length ? Math.round((done / items.length) * 100) : 0;

  const submit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    addCustom(text);
    setText("");
  };

  const copy = () => {
    const body = groups
      .map((g) => `${g.title}\n${g.items.map((i) => `${i.checked ? "[x]" : "[ ]"} ${i.text}`).join("\n")}`)
      .join("\n\n");
    navigator.clipboard.writeText(`Shopping list\n\n${body}`).then(() => toast("success", "List copied!"));
  };

  const print = () =>
    printShoppingList(
      groups.map((g) => ({
        title: g.title,
        items: g.items.map((i) => ({ text: i.text, checked: i.checked })),
      }))
    );

  const confirmClearAll = async () => {
    const r = await Swal.fire({
      title: "Clear the whole list?",
      text: "This removes every item, including unchecked ones.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Clear list",
    });
    if (r.isConfirmed) clearAll();
  };

  return (
    <main className={`shopping-container ${theme ? "dark" : ""}`}>
      <header className="page-header">
        <span className="page-eyebrow">
          {items.length ? `${items.length - done} to buy` : "Ready when you are"}
        </span>
        <h1>Shopping List</h1>
      </header>

      <form className="shop-add" onSubmit={submit}>
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Add an item, e.g. 2 lemons"
          aria-label="Add an item"
        />
        <button type="submit" disabled={!text.trim()}>
          <Plus size={16} /> Add
        </button>
      </form>

      {items.length === 0 ? (
        <EmptyState
          icon={ShoppingBasket}
          title="Your list is empty"
          text="Open any recipe and tap “Shopping list” to add its ingredients, or add your own above."
          action={{ label: "Browse recipes", to: "/" }}
        />
      ) : (
        <>
          <div className="shop-toolbar">
            <div className="shop-progress" aria-label={`${percent}% done`}>
              <div className="shop-progress-bar"><span style={{ width: `${percent}%` }} /></div>
              <small>{done} of {items.length} done</small>
            </div>
            <div className="shop-actions">
              <button onClick={copy}><Copy size={15} /> Copy</button>
              <button onClick={print}><Printer size={15} /> Print</button>
              <button onClick={clearChecked} disabled={done === 0}><Check size={15} /> Clear checked</button>
              <button className="danger" onClick={confirmClearAll}><Trash2 size={15} /> Clear all</button>
            </div>
          </div>

          <div className="shop-groups">
            {groups.map((g) => (
              <section key={g.key} className="shop-group">
                <h2>{g.title}</h2>
                <ul>
                  {g.items.map((item) => (
                    <li key={item.id} className={item.checked ? "done" : ""}>
                      <label>
                        <input type="checkbox" checked={item.checked} onChange={() => toggle(item.id)} />
                        <span>{item.text}</span>
                      </label>
                      <button onClick={() => remove(item.id)} aria-label={`Remove ${item.text}`}>
                        <X size={15} />
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </>
      )}
    </main>
  );
};

export default ShoppingList;
